import React, { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight } from 'lucide-react';

import type { EventDeviceRow, EventRaceSummaryRow } from '../types';
import type { RaceStatusAdvanceOptions } from '../types/EventsOverviewTypes';
import { parseDevicePackageAssignmentsCsvToEventDeviceRows } from '../utils/athleteAssignmentsCsvParse';
import { computeDevicesPendingRetrieval } from '../utils/raceRetrievalCounts';
import { extractAthletesRosterRowCount } from '../utils/athleteAssignmentsMeta';
import {
  formatStatusLabel,
  getNextRaceStatus,
  getPrevRaceStatus,
  getRaceStatusBadgeClass,
  isRaceAssignmentsLocked,
  isRaceRecallEditable,
  isRaceTerminalForDeviceHistory,
  fetchPackagingRacesDistributionReadiness,
  isCombinedModeRace,
  isPackagingToDistributionFullyReady,
  normalizeRaceStatus,
  raceHasUploadedAthletesRoster,
  eventHasAssignableDeviceRaces,
  isDraftRaceStatus,
  type PackagingToDistributionReadinessCheck,
  type RaceConfigurationRequirements,
  dispatchToastError,
} from '../utils';
import { showToastErrorMessage } from '../../../helpers/toastError';
import { isRaceConfigurationReadyForDeviceVerification } from '../../../helpers/deviceValidation';
import RaceValidationReportDialog from '../dialogs/RaceValidationReportDialog';
import { RaceDetailPortal } from './RaceDetailPortal';
import RefreshButton from '../../shared/RefreshButton';
import InlineSpinner from '../../shared/InlineSpinner';
import {
  buildRaceDetailActionGroups,
  buildRaceDetailPortalModel,
  clearValidationReportViewed,
  readValidationReportViewed,
  writeValidationReportViewed,
} from '../utils/raceDetailPortalHelpers';
import BarcodeScannerEntryField from '../../shared/BarcodeScannerEntryField';
import useBarcodeScanner from '../../shared/useBarcodeScanner';
import type { ScannerEntryMode } from '../../shared/types/ScannerTypes';
import {
  looksLikeRacerangerDeviceScanAttempt,
  NOT_RACERANGER_DEVICE_SCAN_TOAST,
  resolveFleetDeviceScanForAssignedDevices,
} from '../../../helpers/racerangerDeviceScan';
import { formatTs, formatUserIdentityDisplay } from '../../../helpers/helpers';

function draftPackagingForwardTooltip(
  hasAthletesRoster: boolean,
  configReady: boolean,
  configLoaded: boolean,
): string | undefined {
  if (!configLoaded) return 'Loading race prerequisites…';
  if (hasAthletesRoster && configReady) return undefined;
  return 'Must upload athlete list and set race configuration (firmware version) before moving to PACKAGING.';
}

function getStageScanKey(raceId: string, raceStatus?: string | null): string {
  return `${String(raceId ?? '').trim()}#${normalizeRaceStatus(raceStatus ?? '')}`;
}

function isRetrievedDeviceRow(row: EventDeviceRow): boolean {
  return Number(row.retrieved ?? row.recalled ?? 0) === 1;
}

function isUnusedDeviceRow(row: EventDeviceRow): boolean {
  return String(row.assignmentStatus ?? '').trim().toUpperCase() === 'UNUSED';
}

function isReadinessFetchError(
  v: PackagingToDistributionReadinessCheck[] | { error: string } | undefined,
): v is { error: string } {
  return !!v && typeof v === 'object' && 'error' in v && typeof (v as { error: string }).error === 'string';
}

type Props = {
  eventId: string;
  wtEventId?: string;
  /** When the event is soft-deleted, races cannot be mutated. */
  eventDeleted: boolean;
  /** From refreshed event details (preferred). */
  races: EventRaceSummaryRow[];
  isAdmin: boolean;
  isOperator?: boolean;
  /** Operators and admins may advance race status (API allows event members). */
  canProgressRaces: boolean;
  onRacesChanged?: (raceId?: string) => void | Promise<void>;
  progressingRaceKey: string | null;
  onAdvanceRace?: (
    eventId: string,
    race: EventRaceSummaryRow,
    nextStatus: string,
    options?: RaceStatusAdvanceOptions,
  ) => void;
  onSoftDeleteRace?: (eventId: string, raceId: string, raceName?: string) => void;
  onHardDeleteRace?: (eventId: string, raceId: string, raceName?: string) => void;
  /** `raceId` null/undefined = all races for the event. */
  onNavigateToDevices?: (
    raceId?: string | null,
    options?: { unretrievedOnly?: boolean },
  ) => void;
  onOpenDevicePackaging?: (eventId: string, raceId: string) => void;
  onOpenRecall?: (eventId: string, raceId: string) => void;
  /** Devices for this event (all races); shown per race when a row is expanded. */
  devices: EventDeviceRow[];
  eventStatus: string;
  globalUserIdToDisplayName: Record<string, string>;
  statusReloadKey: number;
  isDetailsLoaded: boolean;
  isRefreshingEventDetails: boolean;
  onSetDeviceRecalled?: (
    eventId: string,
    deviceId: string,
    recalled: 0 | 1,
    options?: { raceId?: string; retrievedPhase?: string },
  ) => void;
};

export function EventRacesPanel(props: Props): ReactElement {
  const {
    eventId,
    wtEventId,
    eventDeleted,
    races,
    isAdmin,
    isOperator = false,
    canProgressRaces,
    onRacesChanged,
    progressingRaceKey,
    onAdvanceRace,
    onSoftDeleteRace,
    onHardDeleteRace,
    onNavigateToDevices,
    onOpenDevicePackaging,
    onOpenRecall,
    devices,
    eventStatus,
    globalUserIdToDisplayName,
    statusReloadKey,
    isDetailsLoaded,
    isRefreshingEventDetails,
    onSetDeviceRecalled,
  } = props;

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCombinedMode, setNewCombinedMode] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [wtProgramId, setWtProgramId] = useState('');
  const [wtProgramsLoading, setWtProgramsLoading] = useState(false);
  const [wtPrograms, setWtPrograms] = useState<Array<{ programId: string; name: string; date?: string }>>([]);
  const [selectedRaceId, setSelectedRaceId] = useState<string | null>(null);
  /** When API returns no devices for a COMPLETE/DELETED race, rows parsed from latest athlete CSV. */
  const [terminalCsvRowsByRaceId, setTerminalCsvRowsByRaceId] = useState<Record<string, EventDeviceRow[]>>({});
  const [terminalCsvLoadedByRaceId, setTerminalCsvLoadedByRaceId] = useState<Record<string, boolean>>({});
  const terminalCsvFetchAttempted = useRef<Set<string>>(new Set());
  const athletesRosterFetchAttempted = useRef<Set<string>>(new Set());
  const [athletesRosterCountByRaceId, setAthletesRosterCountByRaceId] = useState<Record<string, number>>(
    {},
  );
  const [athletesRosterCountLoadedByRaceId, setAthletesRosterCountLoadedByRaceId] = useState<
    Record<string, boolean>
  >({});
  const readinessFetchGen = useRef(0);

  const [readinessByRaceId, setReadinessByRaceId] = useState<
    Record<string, PackagingToDistributionReadinessCheck[] | { error: string }>
  >({});
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [raceConfigurationByRaceId, setRaceConfigurationByRaceId] = useState<
    Record<string, RaceConfigurationRequirements | null>
  >({});
  const [raceConfigurationLoadedByRaceId, setRaceConfigurationLoadedByRaceId] = useState<
    Record<string, boolean>
  >({});
  const [validationReportRaceId, setValidationReportRaceId] = useState<string | null>(null);
  const [validationReportViewedByRaceId, setValidationReportViewedByRaceId] = useState<
    Record<string, boolean>
  >({});
  const [stageScanValueByStageKey, setStageScanValueByStageKey] = useState<Record<string, string>>({});
  const [stageScanModeByStageKey, setStageScanModeByStageKey] = useState<Record<string, ScannerEntryMode>>({});
  const [stageScanErrorByStageKey, setStageScanErrorByStageKey] = useState<Record<string, string>>({});
  const [stageScanBusyByStageKey, setStageScanBusyByStageKey] = useState<Record<string, boolean>>({});
  const [activeStageScanContext, setActiveStageScanContext] = useState<{ raceId: string; raceStatus: string } | null>(null);

  const loadWtPrograms = useCallback(async (): Promise<void> => {
    const eventIdForWt = String(wtEventId ?? '').trim();
    if (!eventIdForWt || !window.eventsAPI?.listWorldTriathlonPrograms) {
      setWtPrograms([]);
      setWtProgramId('');
      return;
    }
    setWtProgramsLoading(true);
    try {
      const res = await window.eventsAPI.listWorldTriathlonPrograms(eventIdForWt);
      if (!res?.ok) {
        window.dispatchEvent(
          new CustomEvent('toast-error', {
            detail: res?.error || 'Failed to load World Triathlon programs',
          }),
        );
        return;
      }
      const rowsRaw = (res.data as { programs?: unknown[] } | undefined)?.programs;
      const rows = Array.isArray(rowsRaw)
        ? rowsRaw
          .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
          .map((r) => ({
            programId: String(r.prog_id ?? '').trim(),
            name:
              String(r.prog_name ?? '').trim() ||
              String(r.program_name ?? '').trim() ||
              String(r.prog_id ?? '').trim(),
            date: typeof r.prog_date === 'string' ? r.prog_date : undefined,
          }))
          .filter((r) => r.programId)
        : [];
      setWtPrograms(rows);
    } catch (err: any) {
      dispatchToastError(err, 'Failed to load World Triathlon programs');
    } finally {
      setWtProgramsLoading(false);
    }
  }, [wtEventId]);

  useEffect(() => {
    void loadWtPrograms();
  }, [loadWtPrograms]);

  useEffect(() => {
    if (!wtProgramId) return;
    const selected = wtPrograms.find((p) => p.programId === wtProgramId);
    if (!selected) return;
    if (!newName.trim()) {
      setNewName(selected.name);
    }
  }, [wtProgramId, wtPrograms, newName]);

  async function handleCreate(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (eventDeleted || !window.eventsAPI?.createEventRace) return;
    const name = newName.trim();
    if (!name) {
      window.dispatchEvent(
        new CustomEvent('toast-error', { detail: 'Enter a race name before creating.' }),
      );
      return;
    }
    setCreating(true);
    try {
      const selectedProg = wtProgramId ? wtPrograms.find((p) => p.programId === wtProgramId) : undefined;
      const res = await window.eventsAPI.createEventRace(eventId, {
        name,
        raceId: wtProgramId || undefined,
        wtProgramId: wtProgramId || undefined,
        wtProgramName: selectedProg?.name?.trim() || undefined,
        combinedMode: newCombinedMode || undefined,
      });
      if (!res?.ok) {
        window.dispatchEvent(
          new CustomEvent('toast-error', {
            detail: res?.error || 'Failed to create race',
          }),
        );
        return;
      }
      setNewName('');
      setNewCombinedMode(false);
      setWtProgramId('');
      setCreateDialogOpen(false);
      window.dispatchEvent(new CustomEvent('toast-info', { detail: 'Race created' }));
      await onRacesChanged?.();
    } finally {
      setCreating(false);
    }
  }

  const sortedRaces = useMemo(
    () =>
      [...races].sort((a, b) => {
        const ca = Number(a.createdAt) || 0;
        const cb = Number(b.createdAt) || 0;
        return cb - ca;
      }),
    [races],
  );

  const devicesByRaceId = useMemo(() => {
    const m = new Map<string, EventDeviceRow[]>();
    for (const d of devices) {
      const rid = String(d.raceId ?? '').trim();
      if (!rid) continue;
      if (!m.has(rid)) m.set(rid, []);
      m.get(rid)!.push(d);
    }
    return m;
  }, [devices]);

  useEffect(() => {
    terminalCsvFetchAttempted.current.clear();
    athletesRosterFetchAttempted.current.clear();
    setTerminalCsvRowsByRaceId({});
    setTerminalCsvLoadedByRaceId({});
    setAthletesRosterCountByRaceId({});
    setAthletesRosterCountLoadedByRaceId({});
  }, [eventId]);

  useEffect(() => {
    if (!isDetailsLoaded) return;
    const api = window.eventsAPI?.getDevicePackageAssignmentsCsv;

    for (const r of sortedRaces) {
      const rid = String(r.raceId ?? '').trim();
      if (!rid) continue;
      const st = String(r.status ?? '').trim();
      if (!isRaceTerminalForDeviceHistory(st)) continue;
      if ((devicesByRaceId.get(rid) ?? []).length > 0) continue;

      const attemptKey = `${eventId}#${rid}`;
      if (terminalCsvFetchAttempted.current.has(attemptKey)) continue;
      terminalCsvFetchAttempted.current.add(attemptKey);

      if (!api) {
        setTerminalCsvRowsByRaceId((prev) => ({ ...prev, [rid]: [] }));
        setTerminalCsvLoadedByRaceId((prev) => ({ ...prev, [rid]: true }));
        continue;
      }

      void api(eventId, { raceId: rid }).then((res) => {
        const csvText = res?.ok && res.data?.csvText != null ? String(res.data.csvText) : '';
        const rows = csvText ? parseDevicePackageAssignmentsCsvToEventDeviceRows(csvText, rid) : [];
        setTerminalCsvRowsByRaceId((prev) => ({ ...prev, [rid]: rows }));
        setTerminalCsvLoadedByRaceId((prev) => ({ ...prev, [rid]: true }));
      });
    }
  }, [isDetailsLoaded, eventId, sortedRaces, devicesByRaceId]);

  useEffect(() => {
    athletesRosterFetchAttempted.current.clear();
  }, [statusReloadKey]);

  useEffect(() => {
    if (!eventId.trim()) {
      setValidationReportViewedByRaceId({});
      return;
    }
    const next: Record<string, boolean> = {};
    for (const r of sortedRaces) {
      const rid = String(r.raceId ?? '').trim();
      if (!rid) continue;
      const st = normalizeRaceStatus(r.status);
      if (st === 'DRAFT' || st === 'DELETED') {
        clearValidationReportViewed(eventId, rid);
        continue;
      }
      if (readValidationReportViewed(eventId, rid)) {
        next[rid] = true;
      }
    }
    setValidationReportViewedByRaceId(next);
  }, [eventId, sortedRaces, statusReloadKey]);

  const markValidationReportViewed = useCallback(
    (raceId: string): void => {
      const rid = String(raceId ?? '').trim();
      if (!rid || !eventId.trim()) return;
      writeValidationReportViewed(eventId, rid);
      setValidationReportViewedByRaceId((prev) => ({ ...prev, [rid]: true }));
    },
    [eventId],
  );

  useEffect(() => {
    if (!isDetailsLoaded || !eventId.trim()) return;
    const api = window.eventsAPI?.getAthletesCsvMeta;
    for (const r of sortedRaces) {
      const rid = String(r.raceId ?? '').trim();
      if (!rid) continue;
      const attemptKey = `${eventId}#${rid}`;
      if (athletesRosterFetchAttempted.current.has(attemptKey)) continue;
      athletesRosterFetchAttempted.current.add(attemptKey);

      if (!api || !raceHasUploadedAthletesRoster(r)) {
        setAthletesRosterCountLoadedByRaceId((prev) => ({ ...prev, [rid]: true }));
        continue;
      }

      void api(eventId, { raceId: rid }).then((res) => {
        const count = extractAthletesRosterRowCount(res);
        if (count != null) {
          setAthletesRosterCountByRaceId((prev) => ({ ...prev, [rid]: count }));
        }
        setAthletesRosterCountLoadedByRaceId((prev) => ({ ...prev, [rid]: true }));
      });
    }
  }, [eventId, isDetailsLoaded, sortedRaces, statusReloadKey]);

  const getDisplayRaceDevices = useCallback(
    (raceId: string, raceStatus: string | undefined) => {
      const rid = String(raceId ?? '').trim();
      const base = devicesByRaceId.get(rid) ?? [];
      if (base.length > 0) {
        return { rows: base, fromAthleteCsvFallback: false, loadingCsvFallback: false };
      }
      if (!isRaceTerminalForDeviceHistory(raceStatus)) {
        return { rows: [], fromAthleteCsvFallback: false, loadingCsvFallback: false };
      }
      if (terminalCsvLoadedByRaceId[rid] !== true) {
        return { rows: [], fromAthleteCsvFallback: false, loadingCsvFallback: true };
      }
      const csvRows = terminalCsvRowsByRaceId[rid] ?? [];
      return {
        rows: csvRows,
        fromAthleteCsvFallback: csvRows.length > 0,
        loadingCsvFallback: false,
      };
    },
    [devicesByRaceId, terminalCsvLoadedByRaceId, terminalCsvRowsByRaceId],
  );

  useEffect(() => {
    if (!isDetailsLoaded || !eventId.trim()) {
      setReadinessByRaceId({});
      setReadinessLoading(false);
      return;
    }

    const planned = sortedRaces.filter(
      (r) => normalizeRaceStatus(r.status) === 'PACKAGING',
    );
    if (planned.length === 0) {
      setReadinessByRaceId({});
      setReadinessLoading(false);
      return;
    }

    const gen = ++readinessFetchGen.current;
    setReadinessLoading(true);

    const payloads = planned
      .map((r) => {
        const rid = String(r.raceId ?? '').trim();
        if (!rid) return null;
        const display = getDisplayRaceDevices(rid, r.status);
        return { raceId: rid, displayRows: display.rows };
      })
      .filter((p): p is { raceId: string; displayRows: EventDeviceRow[] } => p != null);

    void fetchPackagingRacesDistributionReadiness(eventId, payloads).then((res) => {
      if (readinessFetchGen.current !== gen) return;
      setReadinessByRaceId(res);
      setReadinessLoading(false);
    });

    return () => {
      readinessFetchGen.current += 1;
    };
  }, [isDetailsLoaded, eventId, sortedRaces, getDisplayRaceDevices]);

  useEffect(() => {
    const handler = (e: Event): void => {
      const detail = (e as CustomEvent<{ eventId?: string }>).detail;
      if (String(detail?.eventId ?? '').trim() !== String(eventId ?? '').trim()) return;
      const gen = ++readinessFetchGen.current;
      setReadinessLoading(true);
      const planned = sortedRaces.filter((r) => normalizeRaceStatus(r.status) === 'PACKAGING');
      const payloads = planned
        .map((r) => {
          const rid = String(r.raceId ?? '').trim();
          if (!rid) return null;
          const display = getDisplayRaceDevices(rid, r.status);
          return { raceId: rid, displayRows: display.rows };
        })
        .filter((p): p is { raceId: string; displayRows: EventDeviceRow[] } => p != null);
      void fetchPackagingRacesDistributionReadiness(eventId, payloads).then((res) => {
        if (readinessFetchGen.current !== gen) return;
        setReadinessByRaceId(res);
        setReadinessLoading(false);
      });
    };
    window.addEventListener('fleet-packaging-command-checklist-changed', handler);
    return () => window.removeEventListener('fleet-packaging-command-checklist-changed', handler);
  }, [eventId, sortedRaces, getDisplayRaceDevices]);

  useEffect(() => {
    const api = (window.eventsAPI as { getRaceConfiguration?: (p: { eventId: string; raceId: string }) => Promise<unknown> } | undefined)
      ?.getRaceConfiguration;
    if (!api || !isDetailsLoaded || sortedRaces.length === 0) {
      setRaceConfigurationByRaceId({});
      setRaceConfigurationLoadedByRaceId({});
      return;
    }
    let cancelled = false;
    const raceIds = sortedRaces
      .map((r) => String(r.raceId ?? '').trim())
      .filter(Boolean);
    void Promise.all(
      raceIds.map((raceId) =>
        api({ eventId, raceId }).then((res: unknown) => {
          if (cancelled) return { raceId, cfg: null as RaceConfigurationRequirements | null };
          const r = res as { ok?: boolean; data?: Record<string, unknown> } | null | undefined;
          if (!r?.ok) return { raceId, cfg: null };
          const raw = (r.data?.configuration ?? {}) as Record<string, unknown>;
          const cfg: RaceConfigurationRequirements = {
            fw_version: typeof raw.fw_version === 'string' ? raw.fw_version.trim() : '',
            fw_version_front: typeof raw.fw_version_front === 'string' ? raw.fw_version_front.trim() : '',
            min_battery: Number.isFinite(Number(raw.min_battery)) ? Number(raw.min_battery) : undefined,
            max_debug_size: Number.isFinite(Number(raw.max_debug_size)) ? Number(raw.max_debug_size) : undefined,
            max_race_size: Number.isFinite(Number(raw.max_race_size)) ? Number(raw.max_race_size) : undefined,
          };
          return { raceId, cfg };
        }),
      ),
    )
      .then((rows) => {
        if (cancelled) return;
        const cfgMap: Record<string, RaceConfigurationRequirements | null> = {};
        const loadedMap: Record<string, boolean> = {};
        for (const { raceId, cfg } of rows) {
          cfgMap[raceId] = cfg;
          loadedMap[raceId] = true;
        }
        setRaceConfigurationByRaceId(cfgMap);
        setRaceConfigurationLoadedByRaceId(loadedMap);
      })
      .catch(() => {
        if (!cancelled) {
          setRaceConfigurationByRaceId({});
          setRaceConfigurationLoadedByRaceId({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [eventId, isDetailsLoaded, sortedRaces, statusReloadKey]);

  const orphanRaceDevices = useMemo(
    () => devices.filter((d) => !String(d.raceId ?? '').trim()),
    [devices],
  );

  const showEventDevicesNavigation = eventHasAssignableDeviceRaces(sortedRaces);

  const openRaceDetail = useCallback((raceId: string, openRetrieval = false) => {
    setSelectedRaceId(raceId);
    if (openRetrieval) {
      const race = sortedRaces.find((x) => String(x.raceId ?? '').trim() === raceId);
      const st = normalizeRaceStatus(race?.status ?? '');
      if (st === 'DISTRIBUTION' || st === 'RACKING' || st === 'ACTIVE' || st === 'REPORTING') {
        setActiveStageScanContext({ raceId, raceStatus: st });
      }
    }
  }, [sortedRaces]);

  const closeRaceDetail = useCallback(() => {
    setSelectedRaceId(null);
    setActiveStageScanContext(null);
  }, []);

  const markDeviceRetrieved = useCallback(async (raceId: string, rawDeviceId: string, raceStatus?: string): Promise<void> => {
    const rid = String(raceId ?? '').trim();
    const raw = String(rawDeviceId ?? '').trim();
    if (!rid || !raw) return;

    const race = sortedRaces.find((r) => String(r.raceId ?? '').trim() === rid);
    const stage = normalizeRaceStatus(raceStatus ?? race?.status ?? '');
    const stageKey = getStageScanKey(rid, stage);
    const displayForScan = getDisplayRaceDevices(rid, race?.status);
    const eligibleRows = stage === 'RACKING'
      ? displayForScan.rows.filter((row) => !isRetrievedDeviceRow(row))
      : displayForScan.rows;
    const assignedIds = new Set(
      eligibleRows.map((d) => String(d.deviceId ?? '').trim()).filter(Boolean),
    );
    const deviceId = resolveFleetDeviceScanForAssignedDevices(raw, assignedIds);
    if (!deviceId) {
      const scanHint = looksLikeRacerangerDeviceScanAttempt(raw)
        ? NOT_RACERANGER_DEVICE_SCAN_TOAST
        : stage === 'RACKING'
          ? `${raw} is not eligible for racking; it may already have been retrieved.`
          : `${raw} is not assigned to this race. Scan a device on this race or a Raceranger device QR.`;
      setStageScanErrorByStageKey((prev) => ({
        ...prev,
        [stageKey]: scanHint,
      }));
      return;
    }
    const isAlreadyRetrieved = displayForScan.rows.some(
      (row) => String(row.deviceId ?? '').trim() === deviceId && isRetrievedDeviceRow(row),
    );
    if (isAlreadyRetrieved) {
      setStageScanErrorByStageKey((prev) => ({
        ...prev,
        [stageKey]: `${deviceId} is already marked retrieved.`,
      }));
      return;
    }
    if (!onSetDeviceRecalled) {
      setStageScanErrorByStageKey((prev) => ({
        ...prev,
        [stageKey]: 'Retrieval API is unavailable in this view.',
      }));
      return;
    }

    setStageScanBusyByStageKey((prev) => ({ ...prev, [stageKey]: true }));
    try {
      await Promise.resolve(onSetDeviceRecalled(eventId, deviceId, 1, {
        raceId: rid,
        retrievedPhase: stage,
      }));
      setStageScanValueByStageKey((prev) => ({ ...prev, [stageKey]: '' }));
      setStageScanErrorByStageKey((prev) => ({ ...prev, [stageKey]: '' }));
    } catch {
      setStageScanErrorByStageKey((prev) => ({
        ...prev,
        [stageKey]: `Failed to mark ${deviceId} retrieved.`,
      }));
    } finally {
      setStageScanBusyByStageKey((prev) => ({ ...prev, [stageKey]: false }));
    }
  }, [eventId, getDisplayRaceDevices, onSetDeviceRecalled, sortedRaces]);

  const markDeviceUnretrieved = useCallback(async (raceId: string, deviceId: string, raceStatus?: string): Promise<void> => {
    const rid = String(raceId ?? '').trim();
    const did = String(deviceId ?? '').trim();
    if (!rid || !did) return;

    const race = sortedRaces.find((r) => String(r.raceId ?? '').trim() === rid);
    const stage = normalizeRaceStatus(raceStatus ?? race?.status ?? '');
    const stageKey = getStageScanKey(rid, stage);
    const display = getDisplayRaceDevices(rid, race?.status);
    const row = display.rows.find((r) => String(r.deviceId ?? '').trim() === did);
    if (!row || !isRetrievedDeviceRow(row)) {
      setStageScanErrorByStageKey((prev) => ({
        ...prev,
        [stageKey]: `${did} is not marked retrieved.`,
      }));
      return;
    }
    if (!onSetDeviceRecalled) {
      setStageScanErrorByStageKey((prev) => ({
        ...prev,
        [stageKey]: 'Retrieval API is unavailable in this view.',
      }));
      return;
    }

    setStageScanBusyByStageKey((prev) => ({ ...prev, [stageKey]: true }));
    try {
      await Promise.resolve(onSetDeviceRecalled(eventId, did, 0));
      setStageScanErrorByStageKey((prev) => ({ ...prev, [stageKey]: '' }));
    } catch {
      setStageScanErrorByStageKey((prev) => ({
        ...prev,
        [stageKey]: `Failed to mark ${did} unretrieved.`,
      }));
    } finally {
      setStageScanBusyByStageKey((prev) => ({ ...prev, [stageKey]: false }));
    }
  }, [eventId, getDisplayRaceDevices, onSetDeviceRecalled, sortedRaces]);

  const markDeviceLostAndRetrieved = useCallback(async (raceId: string, deviceId: string, raceStatus?: string): Promise<void> => {
    const rid = String(raceId ?? '').trim();
    const did = String(deviceId ?? '').trim();
    if (!rid || !did) return;

    const race = sortedRaces.find((r) => String(r.raceId ?? '').trim() === rid);
    const stage = normalizeRaceStatus(raceStatus ?? race?.status ?? '');
    const stageKey = getStageScanKey(rid, stage);
    if (stage !== 'ACTIVE') {
      setStageScanErrorByStageKey((prev) => ({
        ...prev,
        [stageKey]: 'Lifecycle lost shortcut is only available in Active.',
      }));
      return;
    }
    if (!window.eventsAPI?.setEventDeviceLostAndRetrieved) {
      setStageScanErrorByStageKey((prev) => ({
        ...prev,
        [stageKey]: 'Lost-retrieval API is unavailable in this view.',
      }));
      return;
    }

    setStageScanBusyByStageKey((prev) => ({ ...prev, [stageKey]: true }));
    try {
      const res = await window.eventsAPI.setEventDeviceLostAndRetrieved({
        eventId,
        raceId: rid,
        deviceId: did,
        reason: 'Marked lost during race retrieval',
      });
      if (!res?.ok) {
        throw new Error(String(res?.error ?? `Failed to set ${did} as retrieved (lost).`));
      }
      if (onRacesChanged) await Promise.resolve(onRacesChanged());
      setStageScanErrorByStageKey((prev) => ({ ...prev, [stageKey]: '' }));
    } catch (err: any) {
      setStageScanErrorByStageKey((prev) => ({
        ...prev,
        [stageKey]: err?.message || `Failed to mark ${did} as retrieved (lost).`,
      }));
    } finally {
      setStageScanBusyByStageKey((prev) => ({ ...prev, [stageKey]: false }));
    }
  }, [eventId, onRacesChanged, sortedRaces]);

  const {
    connectionActive: stageScannerConnected,
    connectionPort: stageScannerPort,
    claim: claimStageScanner,
  } = useBarcodeScanner({
    // The shared scanner hook now arbitrates the physical port by focus/ownership
    // (see useBarcodeScanner's claim mechanism), so this can stay enabled even during
    // Distribution/Racking — focusing this field claims the port from the master
    // collection+racking table's scanner, and vice versa.
    enabled: !!activeStageScanContext,
    mapScan: (value) => {
      const assignedIds = new Set(
        (activeStageScanContext
          ? getDisplayRaceDevices(activeStageScanContext.raceId, activeStageScanContext.raceStatus).rows
          : []
        )
          .map((d) => String(d.deviceId ?? '').trim())
          .filter(Boolean),
      );
      return resolveFleetDeviceScanForAssignedDevices(value, assignedIds) ?? value;
    },
    onScan: (value) => {
      if (!activeStageScanContext) return;
      void markDeviceRetrieved(activeStageScanContext.raceId, value, activeStageScanContext.raceStatus);
    },
  });

  /** Combined-mode DISTRIBUTION: hand-out scanner (marks distributed=1 per scan, server-recorded). */

  const renderStagedDeviceLifecycleSection = (
    raceId: string,
    raceStatus: string,
    raceDevices: EventDeviceRow[],
  ): ReactElement => {
    const normalizedStage = normalizeRaceStatus(raceStatus);
    const isDistribution = normalizedStage === 'DISTRIBUTION';
    const isActive = normalizedStage === 'ACTIVE';
    const scanSectionTitle = isDistribution ? 'Undistributed devices' : isActive ? 'Unretrieved devices' : 'Unracked devices';
    const listSectionTitle = 'Retrieved devices';
    const helpText = isDistribution
      ? 'Scan devices that were NOT handed out — they are pulled from the race before Racking.'
      : isActive
        ? 'Scan returned devices. Mark missing devices as lost.'
        : 'Scan returned devices before racking verification.';
    const stageKey = getStageScanKey(raceId, raceStatus);
    const eligibleDevices = isDistribution ? raceDevices : raceDevices.filter((row) => !isRetrievedDeviceRow(row));
    const scanMode = stageScanModeByStageKey[stageKey] ?? 'scan';
    const scanValue = stageScanValueByStageKey[stageKey] ?? '';
    const error = stageScanErrorByStageKey[stageKey] ?? '';
    const busy = !!stageScanBusyByStageKey[stageKey];
    const retrievedRows = raceDevices
      .filter((row) => isRetrievedDeviceRow(row))
      .map((row) => ({
        deviceId: String(row.deviceId ?? '').trim(),
        retrievedAt: Number(row.retrievedAt ?? 0),
        retrievedBy: String(row.retrievedBy ?? '').trim(),
      }))
      .filter((row) => row.deviceId)
      .map((row) => {
        const parts: string[] = [];
        if (row.retrievedAt > 0) {
          const ts = formatTs(row.retrievedAt);
          if (ts) parts.push(ts);
        }
        const by = formatUserIdentityDisplay(row.retrievedBy, globalUserIdToDisplayName);
        if (by && by !== '-') parts.push(`by ${by}`);
        return { ...row, detail: parts.join(' ') };
      })
      .filter(Boolean)
      .sort((a, b) => a.deviceId.localeCompare(b.deviceId));
    const pendingRows = raceDevices
      .filter((row) => !isRetrievedDeviceRow(row))
      .map((row) => ({
        deviceId: String(row.deviceId ?? '').trim(),
        assignedAthleteId: String(row.assignedAthleteId ?? '').trim(),
      }))
      .filter((row) => row.deviceId)
      .sort((a, b) => a.deviceId.localeCompare(b.deviceId));
    const remainingCount = eligibleDevices.filter((row) => !isRetrievedDeviceRow(row)).length;
    const scannerConnectedForStage =
      stageScannerConnected
      && activeStageScanContext?.raceId === raceId
      && activeStageScanContext?.raceStatus === raceStatus;

    return (
      <div className="space-y-3">
        <div className="rounded-lg border border-slate-200 px-3 py-2 text-xs dark:border-slate-700">
          <div className="font-semibold text-slate-900 dark:text-slate-100">{scanSectionTitle}</div>
          <div className="mt-1 text-slate-600 dark:text-slate-300">{helpText}</div>
        </div>
        <BarcodeScannerEntryField
          connected={scannerConnectedForStage}
          value={scanValue}
          onChange={(value) => {
            setStageScanValueByStageKey((prev) => ({ ...prev, [stageKey]: value }));
            setStageScanErrorByStageKey((prev) => ({ ...prev, [stageKey]: '' }));
          }}
          onSubmit={(value) => {
            void markDeviceRetrieved(raceId, value, raceStatus);
          }}
          mode={scanMode}
          onModeChange={(next) => {
            setStageScanModeByStageKey((prev) => ({ ...prev, [stageKey]: next }));
            setStageScanValueByStageKey((prev) => ({ ...prev, [stageKey]: '' }));
          }}
          parseValue={(raw) => {
            const rows = getDisplayRaceDevices(raceId, raceStatus).rows;
            const ids = new Set(rows.map((d) => String(d.deviceId ?? '').trim()).filter(Boolean));
            return resolveFleetDeviceScanForAssignedDevices(raw, ids) ?? raw;
          }}
          onFocus={() => {
            setActiveStageScanContext({ raceId, raceStatus });
            claimStageScanner();
          }}
          placeholderScan="Scan device ID..."
          placeholderManual="Type device ID and press Enter..."
          placeholderDisconnected="Focus to connect scanner"
          disabled={busy}
          loading={busy}
        />
        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800/60 dark:bg-red-950/30 dark:text-red-200">
            {error}
          </div>
        ) : null}
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-200 bg-gray-50 px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-800">
            <span className="font-semibold text-gray-800 dark:text-slate-200">{listSectionTitle}</span>
            <span className="text-gray-500 dark:text-slate-400">
              {retrievedRows.length} retrieved, {remainingCount} pending
            </span>
          </div>
          {retrievedRows.length === 0 ? (
            <div className="px-3 py-4 text-sm text-gray-500 dark:text-slate-400">
              No devices marked retrieved yet.
            </div>
          ) : (
            <ul className="max-h-56 divide-y divide-slate-200 overflow-y-auto dark:divide-slate-800">
              {retrievedRows.map((row) => (
                <li key={row.deviceId} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-gray-900 dark:text-slate-100">{row.deviceId}</div>
                    {row.detail ? (
                      <div className="text-[10px] text-gray-500 dark:text-slate-400">{row.detail}</div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      void markDeviceUnretrieved(raceId, row.deviceId, raceStatus);
                    }}
                    className="shrink-0 rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Mark unretrieved
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {isActive ? (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="flex items-center justify-between border-b border-slate-200 bg-gray-50 px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-800">
              <span className="font-semibold text-gray-800 dark:text-slate-200">Unretrieved devices</span>
              <span className="text-gray-500 dark:text-slate-400">{pendingRows.length} pending</span>
            </div>
            {pendingRows.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 dark:text-slate-400">
                All devices are already retrieved.
              </div>
            ) : (
              <ul className="max-h-56 divide-y divide-slate-200 overflow-y-auto dark:divide-slate-800">
                {pendingRows.map((row) => (
                  <li key={row.deviceId} className="flex items-center justify-between gap-3 px-3 py-2">
                    <div className="min-w-0">
                      <div className="font-mono text-xs text-gray-900 dark:text-slate-100">{row.deviceId}</div>
                      {row.assignedAthleteId ? (
                        <div className="text-[10px] text-gray-500 dark:text-slate-400">
                          Athlete: {row.assignedAthleteId}
                        </div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        void markDeviceLostAndRetrieved(raceId, row.deviceId, raceStatus);
                      }}
                      className="shrink-0 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-700/70 dark:bg-amber-950/30 dark:text-amber-100 dark:hover:bg-amber-900/40"
                    >
                      Add lifecycle: lost
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  const validationReportRace = validationReportRaceId
    ? sortedRaces.find((x) => String(x.raceId ?? '').trim() === validationReportRaceId)
    : undefined;
  const validationReportDevices =
    validationReportRace && validationReportRaceId
      ? getDisplayRaceDevices(
        String(validationReportRace.raceId ?? '').trim(),
        validationReportRace.status,
      ).rows
      : [];
  const selectedRace = selectedRaceId
    ? sortedRaces.find((x) => String(x.raceId ?? '').trim() === selectedRaceId)
    : undefined;

  const selectedRaceDetailModel = useMemo(() => {
    if (!selectedRace || !selectedRaceId) return null;
    const rid = selectedRaceId;
    const st = normalizeRaceStatus(selectedRace.status ?? 'DRAFT');
    const combined = isCombinedModeRace(selectedRace);
    const next = getNextRaceStatus(st, { combinedMode: combined });
    const prev = getPrevRaceStatus(st, { combinedMode: combined });
    const locked = isRaceAssignmentsLocked(st);
    const progressing = progressingRaceKey === `${eventId}#${rid}`;
    const canNavigate =
      canProgressRaces && !eventDeleted && st !== 'DELETED' && !!onAdvanceRace;
    const showForward = canNavigate && !!next;
    const hasAthletesRosterOnServer = raceHasUploadedAthletesRoster(selectedRace);
    const raceCfg = raceConfigurationByRaceId[rid];
    const raceCfgLoaded = !!raceConfigurationLoadedByRaceId[rid];
    const raceCfgReady = isRaceConfigurationReadyForDeviceVerification(raceCfg, raceCfgLoaded);
    const canMoveForwardToPackaging =
      showForward && st === 'DRAFT' && hasAthletesRosterOnServer && raceCfgReady;
    const draftForwardTooltip = draftPackagingForwardTooltip(
      hasAthletesRosterOnServer,
      raceCfgReady,
      raceCfgLoaded,
    );
    const isDraft = isDraftRaceStatus(st);
    const canDevicePackaging =
      !locked && !eventDeleted && st === 'PACKAGING' && !!onOpenDevicePackaging;
    const canShowValidationReport =
      isDetailsLoaded && !isDraft && st !== 'ACTIVE';
    const canNavigateRaceDevices =
      (isAdmin || isOperator) && !!onNavigateToDevices && !isDraft;
    const canMarkStageRetrieved =
      !eventDeleted && (st === 'DISTRIBUTION' || st === 'RACKING' || st === 'ACTIVE' || st === 'REPORTING');
    const canRecall = isAdmin && isRaceRecallEditable(st) && !eventDeleted;
    const canDeleteRace =
      isAdmin && st !== 'DELETED' && !eventDeleted && !!onSoftDeleteRace;
    const canHardDeleteRace =
      isAdmin && st !== 'ACTIVE' && !eventDeleted && !!onHardDeleteRace;
    const display = getDisplayRaceDevices(rid, selectedRace.status);
    const rackedAthleteKeys = new Set<string>();
    for (const row of display.rows) {
      if (isUnusedDeviceRow(row)) continue;
      if (Number(row.racked ?? 0) !== 1) continue;
      const athleteKey = String(row.assignedAthleteId ?? '').trim()
        || `device:${String(row.deviceId ?? '').trim()}`;
      rackedAthleteKeys.add(athleteKey);
    }
    const hasAnyRackedAthlete = rackedAthleteKeys.size > 0;
    const activeNonRetrievedRows = display.rows.filter(
      (row) => !isUnusedDeviceRow(row) && Number(row.retrieved ?? row.recalled ?? 0) !== 1,
    );
    const rackingActiveDevices = activeNonRetrievedRows.length;
    const rackingRackedDevices = activeNonRetrievedRows.filter((row) => Number(row.racked ?? 0) === 1).length;
    const isRackingComplete = st !== 'RACKING' || rackingRackedDevices === rackingActiveDevices;

    const actionGroups = buildRaceDetailActionGroups({
      eventId,
      race: selectedRace,
      raceId: rid,
      status: st,
      next,
      prev,
      eventDeleted,
      isAdmin,
      canProgressRaces,
      progressing,
      canMoveForwardToPackaging,
      draftForwardTooltip,
      hasAthletesRosterOnServer,
      canDevicePackaging,
      canShowValidationReport,
      canNavigateRaceDevices,
      canMarkStageRetrieved,
      canMoveForwardFromRacking: isRackingComplete,
      rackingForwardDisabledReason:
        st === 'RACKING' && !isRackingComplete
          ? `Racking incomplete: ${rackingRackedDevices}/${rackingActiveDevices} active devices are racked.`
          : undefined,
      canRecall,
      canDeleteRace,
      canHardDeleteRace,
      onAdvanceRace,
      onOpenDevicePackaging,
      onOpenRecall,
      onNavigateToDevices,
      onSoftDeleteRace,
      onHardDeleteRace,
      onOpenValidationReport: () => {
        markValidationReportViewed(rid);
        setValidationReportRaceId(rid);
      },
      onOpenStageRetrieval: () => {
        setActiveStageScanContext({ raceId: rid, raceStatus: st });
      },
      onClosePortal: closeRaceDetail,
    });

    return buildRaceDetailPortalModel({
      race: selectedRace,
      displayRows: display.rows,
      fromAthleteCsvFallback: display.fromAthleteCsvFallback,
      loadingCsvFallback: display.loadingCsvFallback,
      athletesCount: athletesRosterCountByRaceId[rid],
      athletesCountLoaded: athletesRosterCountLoadedByRaceId[rid] === true,
      hasAthletesRoster: raceHasUploadedAthletesRoster(selectedRace),
      isDetailsLoaded,
      isRefreshingEventDetails,
      raceCfg,
      raceCfgLoaded,
      raceCfgReady,
      readinessEntry: readinessByRaceId[rid],
      readinessLoading,
      hasAnyRackedAthlete,
      rackingRackedDevices,
      rackingActiveDevices,
      isRackingComplete,
      eventDeleted,
      actionGroups,
      draftForwardTooltip,
      canMoveForwardToPackaging,
      progressing,
      validationReportViewed: !!validationReportViewedByRaceId[rid],
      renderStagedRetrieval:
        canMarkStageRetrieved
          ? () => renderStagedDeviceLifecycleSection(rid, st, display.rows)
          : undefined,
    });
  }, [
    selectedRace,
    selectedRaceId,
    eventId,
    eventDeleted,
    isAdmin,
    isOperator,
    canProgressRaces,
    progressingRaceKey,
    onAdvanceRace,
    onOpenDevicePackaging,
    onOpenRecall,
    onNavigateToDevices,
    onSoftDeleteRace,
    onHardDeleteRace,
    raceConfigurationByRaceId,
    raceConfigurationLoadedByRaceId,
    athletesRosterCountByRaceId,
    athletesRosterCountLoadedByRaceId,
    validationReportViewedByRaceId,
    markValidationReportViewed,
    isDetailsLoaded,
    isRefreshingEventDetails,
    readinessByRaceId,
    readinessLoading,
    getDisplayRaceDevices,
    devices,
    closeRaceDetail,
    activeStageScanContext,
    stageScanValueByStageKey,
    stageScanModeByStageKey,
    stageScanErrorByStageKey,
    stageScanBusyByStageKey,
    stageScannerConnected,
    stageScannerPort,
  ]);

  return (
    <>
      <div className="min-w-0 max-w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-gray-900 dark:text-slate-100 flex flex-wrap items-center gap-2">
              Races
              <span className="text-xs font-normal text-gray-500 dark:text-slate-400">
                ({isDetailsLoaded ? devices.length : isRefreshingEventDetails ? '…' : '-'} assigned)
              </span>
            </div>
            <div className="text-[11px] text-gray-500 dark:text-slate-400 mt-0.5">
              {showEventDevicesNavigation
                ? 'Open a race to run its stages. Assigned devices stay locked to the race until it completes.'
                : 'Move a draft race to Device Packaging to assign devices.'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!eventDeleted ? (
              <button
                type="button"
                className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                onClick={() => setCreateDialogOpen(true)}
              >
                Create race
              </button>
            ) : null}
            <div title="Reload races (refreshes full event details)">
              <RefreshButton
                busy={false}
                onClick={() => {
                  void onRacesChanged?.();
                }}
                disabled={!onRacesChanged}
                idleText="Refresh"
                busyText="…"
                className="inline-flex items-center justify-center rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                busyClassName="inline-flex items-center gap-2 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-slate-400"
              />
            </div>
            {(isAdmin || isOperator) && onNavigateToDevices && showEventDevicesNavigation ? (
              <button
                type="button"
                title="Open Devices tab filtered to this event (all races)"
                className="inline-flex items-center justify-center rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800"
                onClick={() => onNavigateToDevices(null)}
              >
                View All Devices
              </button>
            ) : null}
          </div>
        </div>

        <div className="min-w-0 max-w-full px-4 py-3 space-y-3">
          {eventDeleted ? (
            <p className="text-xs text-gray-500 dark:text-slate-400">This event is deleted — races cannot be edited.</p>
          ) : null}

          {sortedRaces.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-slate-400">No races yet. Create one to assign devices.</p>
          ) : (
            <ul className="space-y-2">
              {sortedRaces.map((r) => {
                const rid = String(r.raceId ?? '').trim();
                if (!rid) return null;
                const st = normalizeRaceStatus(r.status ?? 'DRAFT');
                const combined = isCombinedModeRace(r);
                const isDraft = isDraftRaceStatus(st);
                const progressing = progressingRaceKey === `${eventId}#${rid}`;
                const display = getDisplayRaceDevices(rid, r.status);
                const devicesPendingRetrieval = computeDevicesPendingRetrieval(display.rows, st);
                const hasRoster = raceHasUploadedAthletesRoster(r);
                const athletesCount =
                  hasRoster && athletesRosterCountLoadedByRaceId[rid] === true
                    ? athletesRosterCountByRaceId[rid]
                    : undefined;
                const raceCfg = raceConfigurationByRaceId[rid];
                const raceCfgLoaded = !!raceConfigurationLoadedByRaceId[rid];
                const raceCfgReady = isRaceConfigurationReadyForDeviceVerification(raceCfg, raceCfgLoaded);
                const activeRows = display.rows.filter(
                  (row) => !isUnusedDeviceRow(row) && Number(row.retrieved ?? row.recalled ?? 0) !== 1,
                );
                const rackedCount = activeRows.filter((row) => Number(row.racked ?? 0) === 1).length;

                const readiness = ((): { label: string; tone: 'ok' | 'warn' | 'muted' } | null => {
                  if (st === 'DRAFT') {
                    if (!raceCfgLoaded) return { label: 'Checking…', tone: 'muted' };
                    return hasRoster && raceCfgReady
                      ? { label: 'Ready for Packaging', tone: 'ok' }
                      : { label: 'Needs config + athletes', tone: 'warn' };
                  }
                  if (st === 'PACKAGING') {
                    if (!isDetailsLoaded || display.loadingCsvFallback || readinessLoading) {
                      return { label: 'Checking…', tone: 'muted' };
                    }
                    const entry = readinessByRaceId[rid];
                    if (entry === undefined) return null;
                    if (isReadinessFetchError(entry)) return { label: 'Check failed', tone: 'warn' };
                    return isPackagingToDistributionFullyReady(entry)
                      ? { label: 'Ready for Distribution', tone: 'ok' }
                      : { label: 'Not ready', tone: 'warn' };
                  }
                  if (st === 'RACKING' || (combined && st === 'DISTRIBUTION')) {
                    const ok = activeRows.length > 0 && rackedCount === activeRows.length;
                    return { label: `Racked ${rackedCount}/${activeRows.length}`, tone: ok ? 'ok' : 'warn' };
                  }
                  return null;
                })();

                const subParts: string[] = [];
                const wt = [String(r.wtProgramId ?? '').trim(), r.wtProgramName?.trim()]
                  .filter(Boolean)
                  .join(' — ');
                if (wt) subParts.push(wt);
                if (athletesCount != null) subParts.push(`${athletesCount} athletes`);
                if (!isDraft && devicesPendingRetrieval != null) {
                  subParts.push(`${devicesPendingRetrieval} to retrieve`);
                }

                return (
                  <li key={rid}>
                    <button
                      type="button"
                      onClick={() => openRaceDetail(rid)}
                      title="Open race workspace"
                      className={[
                        'group flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-all',
                        'hover:-translate-y-[1px] hover:border-indigo-300 hover:shadow-sm dark:hover:border-indigo-600',
                        selectedRaceId === rid
                          ? 'border-indigo-300 bg-indigo-50/60 dark:border-indigo-600 dark:bg-indigo-950/30'
                          : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'inline-flex w-24 shrink-0 items-center justify-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                          getRaceStatusBadgeClass(st),
                        ].join(' ')}
                      >
                        {progressing ? 'Updating…' : formatStatusLabel(st)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="truncate text-sm font-semibold text-gray-900 dark:text-slate-100">
                            {r.name?.trim() || rid}
                          </span>
                          {combined ? (
                            <span className="inline-flex shrink-0 items-center rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-violet-800 dark:border-violet-700/60 dark:bg-violet-950/40 dark:text-violet-200">
                              Combined
                            </span>
                          ) : null}
                        </span>
                        {subParts.length ? (
                          <span className="mt-0.5 block truncate text-[11px] text-gray-500 dark:text-slate-400">
                            {subParts.join(' · ')}
                          </span>
                        ) : null}
                      </span>
                      {readiness ? (
                        <span
                          className={[
                            'hidden shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold sm:inline-flex',
                            readiness.tone === 'ok'
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-100'
                              : readiness.tone === 'warn'
                                ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100'
                                : 'border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400',
                          ].join(' ')}
                        >
                          {readiness.label}
                        </span>
                      ) : null}
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-900 transition-colors group-hover:bg-indigo-100 dark:border-indigo-700/60 dark:bg-indigo-950/50 dark:text-indigo-100">
                        Open
                        <ChevronRight className="h-3.5 w-3.5" />
                      </span>
                    </button>
                  </li>
                );
              })}
              {orphanRaceDevices.length > 0 ? (
                <li className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2.5 text-xs text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/30 dark:text-amber-100">
                  <span>
                    {orphanRaceDevices.length} device{orphanRaceDevices.length === 1 ? '' : 's'} assigned to
                    this event without a race.
                  </span>
                  {onNavigateToDevices && showEventDevicesNavigation ? (
                    <button
                      type="button"
                      className="shrink-0 rounded-md border border-amber-300 bg-white px-2 py-1 font-semibold text-amber-900 hover:bg-amber-100 dark:border-amber-700/60 dark:bg-slate-900 dark:text-amber-100 dark:hover:bg-amber-950/50"
                      onClick={() => onNavigateToDevices(null)}
                    >
                      View in Devices
                    </button>
                  ) : null}
                </li>
              ) : null}
            </ul>
          )}
        </div>
      </div>
      <RaceDetailPortal
        open={selectedRaceId != null && selectedRaceDetailModel != null}
        model={selectedRaceDetailModel}
        eventId={eventId}
        isDetailsLoaded={isDetailsLoaded}
        isRefreshingEventDetails={isRefreshingEventDetails}
        statusReloadKey={statusReloadKey}
        isAdmin={isAdmin}
        globalUserIdToDisplayName={globalUserIdToDisplayName}
        onClose={closeRaceDetail}
        onRefresh={onRacesChanged}
      />
      <RaceValidationReportDialog
        open={validationReportRaceId != null}
        onClose={() => setValidationReportRaceId(null)}
        eventId={eventId}
        raceId={validationReportRaceId ?? ''}
        raceName={validationReportRace?.name}
        devices={validationReportDevices}
        statusReloadKey={statusReloadKey}
      />
      {createDialogOpen
        ? createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/70">
            <div className="w-full max-w-lg overflow-hidden rounded-xl border border-slate-300 bg-white text-gray-800 shadow-xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
              <div className="flex items-center justify-between gap-3 border-b border-slate-300 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
                <div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">Create race</div>
                  <div className="text-xs text-gray-500 dark:text-slate-400">Create a race under this event.</div>
                </div>
                <button
                  type="button"
                  onClick={() => setCreateDialogOpen(false)}
                  className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-xs text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800"
                >
                  Close
                </button>
              </div>
              <form onSubmit={(e) => void handleCreate(e)} className="space-y-3 p-4">
                {String(wtEventId ?? '').trim() ? (
                  <div>
                    <label className="block text-[11px] font-medium text-gray-700 dark:text-slate-300">
                      World Triathlon program (optional)
                    </label>
                    <div className="mt-1 flex items-center gap-2">
                      <select
                        value={wtProgramId}
                        onChange={(e) => setWtProgramId(e.target.value)}
                        className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                        disabled={creating || wtProgramsLoading}
                      >
                        <option value="">None</option>
                        {wtPrograms.map((p) => (
                          <option key={p.programId} value={p.programId}>
                            {p.name}
                            {p.date ? ` (${p.date})` : ''}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        onClick={() => void loadWtPrograms()}
                        disabled={creating || wtProgramsLoading}
                      >
                        {wtProgramsLoading ? 'Loading…' : 'Refresh'}
                      </button>
                    </div>
                  </div>
                ) : null}
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 dark:text-slate-300">
                    New race name <span className="text-red-600">*</span>
                  </label>
                  <input
                    name="raceName"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="e.g. Men Elite"
                    required
                    aria-required
                    className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                    disabled={creating}
                  />
                </div>
                <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/60">
                  <input
                    type="checkbox"
                    checked={newCombinedMode}
                    onChange={(e) => setNewCombinedMode(e.target.checked)}
                    disabled={creating}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold text-gray-900 dark:text-slate-100">
                      Combined collection &amp; racking
                    </span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-gray-500 dark:text-slate-400">
                      Collection and racking scans run together in Distribution; the race then goes
                      straight to Active.
                    </span>
                  </span>
                </label>
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-xs font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800"
                    onClick={() => setCreateDialogOpen(false)}
                    disabled={creating}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating || !newName.trim()}
                    className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {creating ? (
                      <>
                        <InlineSpinner size="xs" variant="current" />
                        Creating…
                      </>
                    ) : (
                      'Create race'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )
        : null}
    </>
  );
}
